import {forwardRef, useImperativeHandle, useRef} from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import {useTranslation} from "react-i18next";
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import {observer} from 'mobx-react';
import {latIsValid, longIsValid, unixNow, dStr, tStr, validDateStr, validTimeStr, t2UnixT} from './Util.js';
import {getAutostationWeather, getSunStr} from './Weather.js'
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import {STEP_LOC, STEP_SPEC} from './Util.js';
import CountryCombo from './CountryCombo.js';
import {getCityName} from './Trans.js';
import {COMM_OK} from './WsR2.js';
import Geoloc from './Geoloc.js';

const AddLoc = observer(forwardRef((props, ref) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const gpsRef = useRef();

	function addLocRxWs(status, msg, priv) {
		gStorage.add.setAddrFetchInProg(false);

		if (status !== COMM_OK) {
			alert('addLocRxWs: error', status);
			return;
		}

// 		console.log('addLocRxWs: Rx:', msg);
	
		if (msg.cmd === 'geo') {
			if (msg.op === 'addr') {
				if (msg.result === 'OK') {
					if (msg.data.addr === 'not found') {
						console.log('addLocRxWs: address for coordinates not found!');
					} else {
						let addr;

						if (msg.data.nearest && msg.data.nearest.id !== msg.data.village.id) {
							addr = msg.data.village.fi + ', ' + msg.data.nearest.fi;
						} else {
							addr = msg.data.village.fi;
						}

						if (priv && priv.fullUpdate) {
							// const city = gStorage.lists.citylist.list.filter((c) => c.id === msg.data.city.id);
							cityChange(null, msg.data.city);
						}

						gStorage.add.setAddr(addr);
						gStorage.add.setAddrAutoChanged(true);

						if (gStorage.add.from_voice && priv && priv.fromVoice) {
							setTimeout(() => {
								gStorage.add.setStep(STEP_SPEC);
							}, 400);
						}
					}
				} else {
					console.error('addLocRxWs: address query failed! Result: ', msg.result, msg.data.desc);
				}
			} else if (msg.op === 'city_coord') {
				if (msg.result === 'OK') {
					if (msg.data.coord === 'not found') {
						console.log('addLocRxWs: coordinates not found!');
					} else {
						console.log('addLocRxWs: city_coord:', msg);
						setObsCoordinates(msg.data.city_coord);
					}
				} else {
					console.error('addLocRxWs: coordinate query failed! Result: ', msg.result, msg.data.desc);
				}
			} else {
				console.error('addLocRxWs: unknown geo operation', msg.op);
			}
		}
	}

	function setObsCoordinates(coord) {
		gStorage.add.setPos(coord.lat, coord.lon);
		gStorage.add.setBirdPos(0, 0);
		gStorage.add.setWeather(null);

		gStorage.add.setCoordAutoChanged(true);

		if (gStorage.add.start_d_valid && gStorage.add.start_t_valid &&
		    gStorage.add.start_d_valid && gStorage.add.start_d_str.length) {
			props.askWeather(coord.lat, coord.lon, gStorage.add.start_d_str, gStorage.add.start_t_str, gStorage.add.obs.tz);
		}
	}

	function updateObsAddr() {
// 		let _obs = gStorage.add.obs;

		if (!(gStorage.add.obs.lat_obs.toString().length && gStorage.add.obs.long_obs.toString().length && 
		    latIsValid(gStorage.add.obs.lat_obs / 1000000.0) && longIsValid(gStorage.add.obs.long_obs / 1000000.0) && 
		    !gStorage.add.addrFetchInProg && !gStorage.add.cityFetchInProg && gStorage.conn.online))
			return;

		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'geo',
			op: 'addr',
			data: {
				coord: {
					lat: gStorage.add.obs.lat_obs,
					long: gStorage.add.obs.long_obs,
				},
				city_only_allowed: 0
			},
		};

		gStorage.add.setAddrFetchInProg(true);
		gStorage.conn.ws.send(msg, addLocRxWs);
	}

	function updateObsCity() {
		gpsRef.current?.gpsPos2Obs();
	}

	function updateObsAll(fromVoice) {
		if (!(gStorage.add.obs.lat_obs.toString().length && gStorage.add.obs.long_obs.toString().length && 
		    latIsValid(gStorage.add.obs.lat_obs / 1000000.0) && longIsValid(gStorage.add.obs.long_obs / 1000000.0) && 
		    !gStorage.add.addrFetchInProg && !gStorage.add.cityFetchInProg && gStorage.conn.online))
			return;

		// console.log('updateObsAll');
		
		gStorage.add.setPos(Math.round(gStorage.gps.pos.latitude * 1000000.0), Math.round(gStorage.gps.pos.longitude * 1000000.0));
		gStorage.add.setCoordAutoChanged(true);

		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'geo',
			op: 'addr',
			data: {
				coord: {
					lat: gStorage.add.obs.lat_obs,
					long: gStorage.add.obs.long_obs,
				},
				city_only_allowed: 0
			},
		}

		gStorage.add.setAddrFetchInProg(true);
		gStorage.conn.ws.send(msg, addLocRxWs, {fullUpdate: true, fromVoice: fromVoice});

		setStartToday();
	}

	function addrChange(event) {
		gStorage.add.setAddr(event.target.value);
		gStorage.add.setAddrAutoChanged(false);
	}

	function weatherStr() {
		let i;

		if (!gStorage.add.weather || !gStorage.add.weather.get)
			return '';

		for (i = 0; i < gStorage.add.weather.get.length; i++) {
			if (gStorage.add.weather.get[i].type === 'autostation') {
				return getAutostationWeather(gStorage.add.weather.get[i]);
			}
		}

		return '';
	}

	function sunStr() {
		if (!gStorage.add.weather)
			return '';

// 		console.log('sunStr', props.weather.sun);
		return getSunStr(gStorage.add.weather.sun);
	}

	function sortCityList(a, b) {
		return addLocGetCityName(a).localeCompare(addLocGetCityName(b), 'fi');
	}

	function regChange(event) {
		let regID;

		if (!event.target.value) {
			console.error('regChange', 'no reg value', event.target.value);
			return;
		}

		regID = Number(event.target.value);

		let reg = gStorage.lists.reglist.list.find(x => x.id === regID);
		if (!reg) {
			console.error('regChange', 'reg not found for', event.target.value);
			return;
		}

//		console.log('regChange', reg);

		const _city = gStorage.lists.citylist.list.find((city) => 
			city.id === gStorage.lists.reglist.list.find((dst) => dst.id === reg.id).city_id
		);

		const country = gStorage.lists.countrylist.find(x => x.id === _city.country);
		gStorage.add.setCityList(gStorage.lists.citylist.list.filter(x => x.country === _city.country).sort(sortCityList));
		gStorage.add.setCountry(country);

		gStorage.add.setRegId(regID);

		gStorage.add.setPos(reg.lat_obs, reg.long_obs);
		gStorage.add.setObsCity(_city.id, addLocGetCityName(_city));
		gStorage.add.setCityAutoChanged(false);
		gStorage.add.setAddr(reg.name);
		gStorage.add.setAddrAutoChanged(false);

		gStorage.add.setCity(_city);
		gStorage.add.setWeather(null);

		if (gStorage.add.start_t_valid && gStorage.add.start_t_str.length && 
		    gStorage.add.start_d_valid && gStorage.add.start_d_str.length &&
		    gStorage.add.obs.lat_obs && gStorage.add.obs.long_obs && gStorage.conn.online) {
			props.askWeather(gStorage.add.obs.lat_obs, gStorage.add.obs.long_obs, gStorage.add.start_d_str, gStorage.add.start_t_str, gStorage.add.obs.tz);
		}
	}

	function setStartToday() {
		let d_valid, t_valid, d_str, t_str;
		let e = unixNow();

		d_str = dStr(e);
		d_valid = validDateStr(d_str);

		t_str = tStr(e);
		t_valid = validTimeStr(t_str);

		gStorage.add.setStartDStr(d_str, d_valid);
		gStorage.add.setStartTStr(t_str, t_valid);
		gStorage.add.setWeather(null);

		if (t_valid && d_valid && t_str.length && d_str.length && 
		    gStorage.add.obs.lat_obs && gStorage.add.obs.long_obs && gStorage.conn.online) {
			props.askWeather(gStorage.add.obs.lat_obs, gStorage.add.obs.long_obs, d_str, t_str, gStorage.add.obs.tz);
		}

		gStorage.add.setTimeAutoChanged(true);
	}

	function startDChange(event, value) {
		let d_str, d_valid;

		d_str = event.target.value;
		d_valid = validDateStr(d_str);

		gStorage.add.setStartDStr(d_str, d_valid);
		gStorage.add.setWeather(null);

		if (gStorage.add.start_t_valid && d_valid && gStorage.add.start_t_str.length && d_str.length && 
		    gStorage.add.obs.lat_obs && gStorage.add.obs.long_obs && gStorage.conn.online) {
			props.askWeather(gStorage.add.obs.lat_obs, gStorage.add.obs.long_obs, d_str, gStorage.add.start_t_str, gStorage.add.obs.tz);
		}

		gStorage.add.setTimeAutoChanged(false);
	}

	function cityChange(event, value) {
		// console.log('cityChange', event, value.id);

		if (value) {
			const city = gStorage.lists.citylist.list.find(x => x.id === value.id);
			const country = gStorage.lists.countrylist.find(x => x.id === city.country);

			if (country.id !== gStorage.add.country.id) {
				gStorage.add.setCountry(country);
				gStorage.add.setCityList(gStorage.lists.citylist.list.filter(x => x.country === country.id).sort(sortCityList));
			}

			gStorage.add.setObsCity(value.id);
		} else {
			gStorage.add.setObsCity(0);
		}

		gStorage.add.setCity(value);

		if (event && value) {
			loadCityCoordinates(value.id);
		}

		gStorage.add.setCountryAutoChanged(event ? false : true);
		gStorage.add.setCityAutoChanged(event ? false : true);
		gStorage.add.setAddrAutoChanged(false);
	}

	function loadCityCoordinates(city_id) {
		if (!gStorage.conn.online)
			return;

		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'geo',
			op: 'city_coord',
			data: {
				city: city_id
			},
		}

		gStorage.add.setAddrFetchInProg(true);
		gStorage.conn.ws.send(msg, addLocRxWs);
	}

	function countryChange(event) {
		if (event.target.value !== gStorage.add.country.id) {
			const country = gStorage.lists.countrylist.find(x => x.id === event.target.value);

			console.log('countryChange', event.target.value, country, typeof(event.target.value));

			gStorage.add.setCountry(country);
			gStorage.add.setCityList(gStorage.lists.citylist.list.filter(x => x.country === country.id).sort(sortCityList));
			gStorage.add.setCity(gStorage.add.citylist[0]);
			gStorage.add.setObsCity(gStorage.add.citylist[0].id);

			loadCityCoordinates(gStorage.add.citylist[0].id);

			gStorage.add.setCountryAutoChanged(event ? false : true);
			gStorage.add.setCityAutoChanged(event ? false : true);
			gStorage.add.setAddrAutoChanged(false);
		}
	}

	function addLocGetCityName(city) {
		return getCityName(city, gStorage.conn.user);
	}
	
	function startTChange(event) {
		let t_str = event.target.value;

		gStorage.add.setWeather(null);

		if (validTimeStr(t_str)) {
			if (t2UnixT(t_str) === 0) {
				gStorage.add.setStartTStr('', true);
			} else {
				gStorage.add.setStartTStr(t_str, true);

				if (gStorage.add.start_d_valid && gStorage.add.start_d_str.length &&
				    gStorage.add.obs.lat_obs && gStorage.add.obs.long_obs && gStorage.conn.online) {
					props.askWeather(gStorage.add.obs.lat_obs, gStorage.add.obs.long_obs, gStorage.add.start_d_str, t_str, gStorage.add.obs.tz);
				}
			}
		} else {
			gStorage.add.setStartTStr(event.target.value, false);
		}

		gStorage.add.setTimeAutoChanged(false);
	}

	function addLocRecAction(cmd, rec) {
		switch (cmd) {
		case 'fetch_all':
			updateObsAll(true);
			break;
		case 'city':
			updateObsCity();
			break;
		case 'address':
			updateObsAddr();
			break;
		case 'now':
			setStartToday();

			if (gStorage.add.from_voice) {
				setTimeout(() => {
					gStorage.add.setStep(STEP_SPEC);
				}, 400);
			}

			break;
		default:
			/* Nothing here. */
			break;
		}
	}

	useImperativeHandle(ref, () => ({
		addLocRecAction,
	}));

	if (gStorage.add.currentStep !== STEP_LOC)
		return null;

	const regl = gStorage.lists.dstlist.slice().sort((p1, p2) => {
		if (p1.id === 0)
			return -1;
		if (p2.id === 0)
			return 1;

// console.log('sort', p1.id, p2.id, gStorage.lists.reglist.list.find((dst) => dst.id === p1.id).city_id, gStorage.lists.reglist.list.find((dst) => dst.id === p2.id).city_id);

		const r1 = gStorage.lists.reglist.list.find((dst) => dst.id === p1.id);
		if (!r1)
			return 0;

		const r2 = gStorage.lists.reglist.list.find((dst) => dst.id === p2.id)
		if (!r1)
			return 0;

		let _city1 = gStorage.lists.citylist.list.find((city) => city.id === r1.city_id);
		let _city2 = gStorage.lists.citylist.list.find((city) => city.id === r2.city_id);

		let ret = addLocGetCityName(_city1).localeCompare(addLocGetCityName(_city2), 'fi');
		if (ret)
			return ret;

		return r1.name.localeCompare(r2.name, 'fi');
	});

	const cityBg = gStorage.add.cityAutoChanged ? '#f4fff4' : gStorage.add.cityFetchInProg ? 'lightyellow' : 'white';
	const addrBg = gStorage.add.addrAutoChanged ? '#f4fff4' : gStorage.add.addrFetchInProg ? 'lightyellow' : 'white';
	const timeBg = gStorage.add.timeAutoChanged ? '#f4fff4' : 'white';

	return (
		<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
			<Grid size={{xs: 12}} align={'center'}>
				<FormControl variant="outlined" size="small" style={{marginTop: 8}}>
					<InputLabel>{t('add.reg_tgt')}</InputLabel>
					<Select
						native
						disabled={!regl.length}
						value={gStorage.add.regID}
						onChange={regChange}
						style={{width: 300, margin: 8}}>
							{regl.map((dt, i) => {
								if (dt.id <= 0)
									return (<option key={dt.id} value={dt.id}></option>);

								let _city = gStorage.lists.citylist.list.find((city) => city.id === gStorage.lists.reglist.list.find((dst) => dst.id === dt.id).city_id)
								return (<option key={dt.id} value={dt.id}>{addLocGetCityName(_city) + (dt.id ? (', ' + dt.name) : '')}</option>);
							})}
					</Select>
				</FormControl>
			</Grid>

			<Geoloc ref={gpsRef} cityChange={cityChange} {...props} />

			<Grid size={{xs: 12}} align={'center'}>
				<CountryCombo
					value={gStorage.add.country.id}
					onChange={countryChange}
					error={gStorage.add.obs.city_id <= 0 ? 1 : 0}
					style={{width: 300, margin: 4, backgroundColor: cityBg, textAlign: 'left'}}
					disableClearable
				/>
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
				<Autocomplete
					options={gStorage.add.citylist}
					getOptionLabel={option => addLocGetCityName(option)}
					isOptionEqualToValue={(option, value) => option.id === value.id }
					value={gStorage.add.city}
					onChange={cityChange}
					error={gStorage.add.obs.city_id <= 0 ? 1 : 0}
					autoSelect
					disableClearable
					style={{width: 300, margin: 4, backgroundColor: cityBg}}
					renderInput={params => (
						<TextField {...params} label={t('add.city')} variant="outlined" size="small" fullWidth />
					)} />
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
					<Button 
						variant="contained" 
						color="primary" 
						size="small" 
						style={{width:143, margin: 8}}
						onClick={updateObsAddr}
						disabled={!gStorage.conn.online || gStorage.add.addrFetchInProg || gStorage.add.cityFetchInProg}>
						{t('add.get_addr')}
					</Button>
					<Button 
						variant="contained" 
						color="primary" 
						size="small" 
						style={{width:143, margin: 8}} 
						onClick={() => updateObsAll(false)}
						disabled={!gStorage.conn.online || gStorage.add.addrFetchInProg || gStorage.add.cityFetchInProg || !gStorage.gps.available}>
						{t('add.get_all')}
					</Button>
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
				<TextField 
					label={t('add.address')}
					value={gStorage.add.obs.addr}
					error={!gStorage.add.obs.addr.length}
					onChange={addrChange}
					style={{width: 300, margin: 4, backgroundColor: addrBg}} 
					variant="outlined" 
					disabled={false}
					size="small" />
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
				<FormControl variant="outlined" size="small" style={{width:300}} >
					<Button 
						variant="contained" 
						color="primary" 
						size="small" 
						style={{width:143, marginTop: 4, marginBottom: 4, align: 'left'}} 
						onClick={setStartToday}>{t('add.now')}
					</Button>
				</FormControl>
			</Grid>

			<Grid size={{xs: 12}} align={'center'}>
				<TextField 
					label={t('add.start_date')}
					value={gStorage.add.start_d_str}
					onChange={startDChange}
					style={{width: 143, margin: 4, backgroundColor: timeBg}} 
					variant="outlined" 
					error={!gStorage.add.start_d_valid}
					size="small" />

				<TextField 
					label={t('add.start_time')}
					value={gStorage.add.start_t_str}
					onChange={startTChange}
					style={{width: 143, margin: 4, backgroundColor: timeBg}} 
					variant="outlined" 
					error={!gStorage.add.start_t_valid}
					size="small" />
			</Grid>
			
			<Grid size={{xs: 12}} align={'center'}>
   				<div style={{margin: 8}} >{weatherStr()}</div>
   				<div style={{margin: 8}} >{sunStr()}</div>
			</Grid>
		</Grid>
	);
}));

export default AddLoc;
